'use es6';

// Utilities and helpers to debug selector performance in IPEUI and parent editor apps.
//
// To use them you'll need to:
//   - Run bend with a SELECTOR_STATS=true env variable
//   - Run the editor and look at `hs.DEBUG.gatherStatSummary()` or `hubspot.DEBUG.gatherStatSummary()`
//     and the other variables and functions in that namespace
//
// More info in PR https://git.hubteam.com/HubSpot/ContentEditorUI/pull/5728 and `selector-stat-helpers.js`
function calcAvg(values) {
  return values.reduce((x, y) => x + y, 0) / values.length;
}
function buildStats(recomputations, durations) {
  return {
    avg: calcAvg(durations),
    max: Math.max(...durations),
    min: Math.min(...durations),
    recomputations,
    durations
  };
}
const looksLikeASelector = selectorName => selectorName.startsWith('get') && !selectorName.endsWith('Helper');

// Used some naming heuristics and `ignoredForStats` override (set by `basicSelector`)
// to try and uncover any exported selector functions which have not been "wrapped"
// with `createSelector` or `basicSelectorWithStats`
const looksLikeASelectorAndNotIgnored = (selectorName, selector) => looksLikeASelector(selectorName) && !selector.ignoredForStats;
const hasSelectorStats = selector => selector.recomputations && selector.durations;
export const gatherSelectorStats = allSelectors => {
  const stats = {};
  const selectorsWithoutStats = [];
  for (const selectorPath of Object.keys(allSelectors)) {
    const selectors = allSelectors[selectorPath];
    for (const selectorName of Object.keys(selectors)) {
      const selector = selectors[selectorName];

      // If this selector was created via `createSelector*` (and `custom-reselect.js` is setup)
      if (hasSelectorStats(selector)) {
        const recomputations = selector.recomputations();
        if (recomputations > 0) {
          const durations = selector.durations();
          stats[selectorPath] = stats[selectorPath] || {};
          stats[selectorPath][selectorName] = buildStats(recomputations, durations);
          stats[selectorPath][selectorName].memoized = !selector.notMemoized;
        }
      } else if (looksLikeASelectorAndNotIgnored(selectorName, selector)) {
        selectorsWithoutStats.push(`${selectorName} in ${selectorPath}`);
      }
    }
  }
  if (selectorsWithoutStats.length > 0) {
    console.log('Selectors without stats:', selectorsWithoutStats);
  }
  return stats;
};
export const gatherFlattenedSelectorStats = allSelectors => {
  const result = {};
  const stats = gatherSelectorStats(allSelectors);
  for (const selectorPath of Object.keys(stats)) {
    const selectors = stats[selectorPath];
    for (const selectorName of Object.keys(selectors)) {
      const selectorStats = selectors[selectorName];
      const moduleName = selectorPath.replace(/\.js|\.\//g, '');
      const combinedName = `${moduleName}#${selectorName}`;
      result[combinedName] = Object.assign({
        name: selectorName,
        module: moduleName,
        combinedName
      }, selectorStats);
    }
  }
  return result;
};
export const gatherFlattenedSelectorStatsArray = allSelectors => {
  const stats = gatherFlattenedSelectorStats(allSelectors);
  return Object.keys(stats).map(key => stats[key]);
};
export const gatherSlowestSelectorsByAvg = allFlattenedSelectors => {
  return allFlattenedSelectors.sort((a, b) => b.avg - a.avg);
};
export const gatherSlowestSelectorsByTotalTime = allFlattenedSelectors => {
  return allFlattenedSelectors.sort((a, b) => b.avg * b.recomputations - a.avg * a.recomputations);
};
export const gatherSlowestSelectorsByMax = allFlattenedSelectors => {
  return allFlattenedSelectors.sort((a, b) => b.max - a.max);
};
export const gatherMostCalledMemoizedSelectors = allFlattenedSelectors => {
  return allFlattenedSelectors.filter(s => s.memoized).sort((a, b) => b.recomputations - a.recomputations);
};
export const gatherMostCalledNonMemoizedSelectors = allFlattenedSelectors => {
  return allFlattenedSelectors.filter(s => !s.memoized).sort((a, b) => b.recomputations - a.recomputations);
};
function cleanedStats(stats) {
  const {
    name,
    recomputations,
    avg,
    max,
    module,
    memoized
  } = stats;
  return {
    name,
    calls: recomputations,
    avg: parseFloat(avg.toFixed(4)),
    max: parseFloat(max.toFixed(4)),
    totalTime: parseFloat((avg * recomputations).toFixed(4)),
    module,
    memoized
  };
}
export const gatherStatSummary = (allSelectors, numToPrint = 7) => {
  const flattenedStats = gatherFlattenedSelectorStatsArray(allSelectors);
  const mostCalledMemoized = gatherMostCalledMemoizedSelectors(flattenedStats);
  const mostCalledNonMemozied = gatherMostCalledNonMemoizedSelectors(flattenedStats);
  const slowestMax = gatherSlowestSelectorsByMax(flattenedStats);
  const slowestTotalTime = gatherSlowestSelectorsByTotalTime(flattenedStats);
  const slowestAvg = gatherSlowestSelectorsByAvg(flattenedStats);
  console.log('\nMost called memoized selectors:\n');
  console.table(mostCalledMemoized.slice(0, numToPrint).map(s => {
    const {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    } = cleanedStats(s);
    return {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    };
  }));
  console.log('\nMost called non-memoized selectors:\n');
  console.table(mostCalledNonMemozied.slice(0, numToPrint).map(s => {
    const {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    } = cleanedStats(s);
    return {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    };
  }));
  console.log('\nSlowest selectors (by max):\n');
  console.table(slowestMax.slice(0, numToPrint).map(s => {
    const {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    } = cleanedStats(s);
    return {
      name,
      max,
      avg,
      calls,
      totalTime,
      module,
      memoized
    };
  }));
  console.log('\nSlowest selectors (by total time):\n');
  console.table(slowestTotalTime.slice(0, numToPrint).map(s => {
    const {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    } = cleanedStats(s);
    return {
      name,
      totalTime,
      avg,
      max,
      calls,
      module,
      memoized
    };
  }));
  console.log('\nSlowest selectors (by avg):\n');
  console.table(slowestAvg.slice(0, numToPrint).map(s => {
    const {
      name,
      calls,
      avg,
      max,
      totalTime,
      module,
      memoized
    } = cleanedStats(s);
    return {
      name,
      avg,
      max,
      calls,
      totalTime,
      module,
      memoized
    };
  }));
  console.log('\nMore info:', {
    mostCalledMemoized,
    mostCalledNonMemozied,
    slowestMax,
    slowestAvg
  });
};

// Give each selector calc function an actual name
export const attachFunctionNamesToSelectorResultFuncs = allSelectors => {
  for (const selectorPath of Object.keys(allSelectors)) {
    const selectors = allSelectors[selectorPath];
    for (const selectorName of Object.keys(selectors)) {
      const selector = selectors[selectorName];
      if (!hasSelectorStats(selector) && looksLikeASelectorAndNotIgnored(selectorName, selector)) {
        console.log(`Selector not wrapped: ${selectorName}  (from ${selectorPath})`);
      }
      if (selector.resultFunc) {
        Object.defineProperty(selector.resultFunc, 'name', {
          configurable: true,
          value: `${selectorName}-resultFunc`
        });
      }
    }
  }
};
export const attachStatHelpersToWindow = allSelectors => {
  console.log('Enabling selector stats collection in', window.hubspot.bender.currentProject || 'the inpage editor iframe');
  attachFunctionNamesToSelectorResultFuncs(allSelectors);
  const debugFuncs = {
    allSelectors,
    gatherSelectorStats: gatherSelectorStats.bind(null, allSelectors),
    gatherSlowestSelectorsByAvg: gatherSlowestSelectorsByAvg.bind(null, allSelectors),
    gatherSlowestSelectorsByMax: gatherSlowestSelectorsByMax.bind(null, allSelectors),
    gatherMostCalledMemoizedSelectors: gatherMostCalledMemoizedSelectors.bind(null, allSelectors),
    gatherMostCalledNonMemoizedSelectors: gatherMostCalledNonMemoizedSelectors.bind(null, allSelectors),
    gatherSelectorStatSummary: gatherStatSummary.bind(null, allSelectors)
  };

  // IPEUI
  if (window.hs) {
    window.hs.DEBUG = debugFuncs;
  }

  // Parent editors
  if (window.hubspot) {
    window.hubspot.DEBUG = debugFuncs;
  }
};